import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { Button } from '../../components/Button/Button';
import { BaseLayout } from '../../components/Layout/Layout';
import { TextField } from '../../components/TextField/TextField';
import { useLanguage } from '../../locale/useLanguage';
import { Routes } from '../../routes/types';

import './ConfirmEmailView.css';
import { useAppSelector } from '../../redux/core';
import { isEmailConfirmedSelector } from '../../redux/user/user.selectors';
import {
  useConfirmEmailMutation,
  useResendConfirmationCodeMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import { useResendEmailTimer } from '../../hooks/useResendEmailTimer';
import { parseRes } from '../../apis/utils';
import { handleSignIn } from '../../helpers/auth';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { useSelector } from 'react-redux';

type ConfirmEmailViewProps = {};

type CodeForm = {
  code: string;
};

const confirmNumberRegexp = /\b[\d]{5}\b/;

export const ConfirmEmailView: React.FC<ConfirmEmailViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CodeForm>();

  const [confirmEmail] = useConfirmEmailMutation();
  const operation = useSelector(getOperationSelector);

  const [resendEmailRequest, { fulfilledTimeStamp: resendFulfilledTimeStamp }] =
    useResendConfirmationCodeMutation();

  const isEmailConfirmed = useAppSelector(isEmailConfirmedSelector);

  const { isResendActive, remainingTime } = useResendEmailTimer(
    resendFulfilledTimeStamp
  );

  useEffect(() => {
    if (isEmailConfirmed) {
      history.replace(Routes.Home);
    }
  }, [history, isEmailConfirmed]);

  const codeValidator = useMemo(
    () => ({
      ...register('code', {
        required: {
          value: true,
          message: t('confirmEmailView.codeRequired'),
        },
        pattern: {
          value: confirmNumberRegexp,
          message: t('confirmEmailView.codeNotValid'),
        },
      }),
    }),
    [register, t]
  );

  const confirmCode = useMemo(() => {
    return handleSubmit((datas) => {
      confirmEmail(datas.code).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        handleSignIn(successRes, operation, history);
        if (errorRes.error) {
          // TODO: Handle confirm code error
        }
      });
    });
  }, [confirmEmail, handleSubmit, history, operation]);

  const resendEmail = useCallback(() => {
    resendEmailRequest({});
  }, [resendEmailRequest]);

  return (
    <BaseLayout title={t('pageTitles.confirmEmail')}>
      <div className="confirm-email--body">
        <div>
          <h2 className="subtitle">{t('confirmEmailView.title')}</h2>
          <p className="confirm-email--description paragraph">
            {t('confirmEmailView.description')}
          </p>
          <div className="confirm-email--text-input">
            <TextField
              {...codeValidator}
              error={errors.code?.message}
              placeholder={t('confirmEmailView.emailCodePlaceholder')}
            />
          </div>
        </div>
        <div>
          <Button
            onClick={confirmCode}
            label={t('confirmEmailView.confirm')}
            isPrimary
          />
          <div className="confirm-email--resend-button">
            <Button
              isDisabled={!isResendActive}
              onClick={resendEmail}
              label={t('confirmEmailView.codeNotReceived')}
            />
          </div>
          {isResendActive && (
            <div className="confirm-email--placeholder-timer" />
          )}
          {!isResendActive && (
            <p className="confirm-email--remaining-time">
              {t('confirmEmailView.timerDescription', { time: remainingTime })}
            </p>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};
